import React, { useState, useEffect } from 'react';

import { faCircleNotch, faWarning } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowDown, FileText } from 'lucide-react';

import { useGetFact } from '@/api/queries/useGetFact';
import { Button } from '@/components/atoms/new-button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/atoms/ShadCnSelect';
import Drawer from '@/components/molecules/Drawer';
import { CloseIcon } from '@/components/organisms/PDFViewer/components/CloseIcon';
import DocViewerFullFact from '@/screens/Chronos/CaseEditor/components/DocViewer/components/DocViewerFullFact';
import FactDocViewer from '@/screens/Chronos/CaseEditor/components/DocViewer/FactDocViewer';

interface FactWithDocumentDrawerProps {
  caseId: string | null;
  factId: string | null;
  isOpen: boolean;
  onClose: () => void;
}

const FactWithDocumentDrawer: React.FC<FactWithDocumentDrawerProps> = ({ caseId, factId, isOpen, onClose }) => {
  const { data: fact, isLoading, error } = useGetFact(caseId, factId);
  const [isFactInfoExpanded, setIsFactInfoExpanded] = useState(false);
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null);

  const handleClose = () => {
    setIsFactInfoExpanded(false);
    setSelectedDocId(null);
    onClose();
  };

  const handleDocumentChange = (newDocId: string) => {
    if (newDocId !== selectedDocId) {
      setSelectedDocId(newDocId);
    }
  };

  // When a fact is loaded, automatically select the first document
  useEffect(() => {
    if (fact && fact.locations && fact.locations.length > 0) {
      const firstLocation = fact.locations[0];
      setSelectedDocId(firstLocation.docId);
    }
  }, [fact]);

  return (
    <Drawer isOpen={isOpen} onClose={handleClose} hideCloseButton={true}>
      <div className="flex h-full flex-col">
        <CloseIcon onClick={handleClose} />
        {error ? (
          <div className="my-10 flex h-full w-full flex-col items-center justify-center text-red-600">
            <FontAwesomeIcon icon={faWarning} className="mb-4 text-2xl" />
            <div className="font-semibold">Sorry, there was a problem loading this fact.</div>
            <div className="mt-2 text-xs text-gray-500">Please try again or contact support if the issue persists.</div>
          </div>
        ) : (
          <>
            {isLoading && (
              <div className="my-20 flex h-full w-full items-center justify-center">
                <FontAwesomeIcon icon={faCircleNotch} className="animate-spin" />
              </div>
            )}
            {fact && (
              <>
                {/* Fact Information Section */}
                <div className="border-b border-gray-300 bg-gray-50">
                  <div className="flex items-center gap-3 px-4 py-2">
                    <Button
                      onClick={() => setIsFactInfoExpanded(!isFactInfoExpanded)}
                      variant="secondary"
                      size="xs"
                      className="h-9 w-32"
                    >
                      {isFactInfoExpanded ? 'Hide full fact' : 'Show full fact'}
                      <motion.div
                        animate={{ rotate: isFactInfoExpanded ? 180 : 0 }}
                        transition={{ duration: 0.2, ease: 'easeInOut' }}
                      >
                        <ArrowDown />
                      </motion.div>
                    </Button>
                    {fact.locations && fact.locations.length > 0 && (
                      <div className="flex h-9 items-center gap-2 rounded-md border border-gray-200 bg-white px-3">
                        <div className="flex items-center gap-1.5">
                          <FileText className="h-3.5 w-3.5 text-blue-600" />
                          <span className="text-xs font-medium text-gray-700">Document:</span>
                        </div>
                        {fact.locations.length === 1 ? (
                          <div className="flex h-6 items-center">
                            <span className="max-w-52 truncate text-xs text-gray-700">
                              {fact.locations[0].doc?.file?.name || 'Document'}
                            </span>
                          </div>
                        ) : (
                          <Select value={selectedDocId || ''} onValueChange={handleDocumentChange}>
                            <SelectTrigger className="h-6 w-32 bg-white text-xs shadow-sm transition-colors hover:bg-gray-50">
                              <SelectValue placeholder="Select" />
                            </SelectTrigger>
                            <SelectContent>
                              {fact.locations.map((location) => (
                                <SelectItem key={location.docId} value={location.docId}>
                                  <div className="flex items-center gap-2">
                                    <span className="truncate">{location.doc?.file?.name || 'Document'}</span>
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    )}
                  </div>
                  <AnimatePresence>
                    {isFactInfoExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="max-h-80 overflow-y-auto px-4 pb-4">
                          <DocViewerFullFact fact={fact} showComments={false} showDocumentDetails={false} />
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Document Viewer Section */}
                <div className="flex-1 overflow-hidden">
                  <AnimatePresence mode="wait">
                    {selectedDocId ? (
                      <motion.div
                        key={selectedDocId}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                        className="h-full"
                      >
                        <FactDocViewer
                          caseId={caseId}
                          docId={selectedDocId}
                          factId={factId}
                          hideFeatureButtons={false}
                          hideCloseButton={true}
                        />
                      </motion.div>
                    ) : (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="flex h-full items-center justify-center text-gray-500"
                      >
                        <p>Select a document to view</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </Drawer>
  );
};

export default FactWithDocumentDrawer;
